import React, { useMemo, useRef } from "react";
import {
  Checkbox,
  FormControlLabel,
  Slider,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import {
  TRAIN_RDV_OPT_COLLAPSE,
  TRAIN_RDV_OPT_EXPAND,
  buildTrainRdvPartition,
} from "../../../lib/mfBulkInput/rdvPartition";
import { TRAIN_NON_FIRE_RATIO } from "../../../lib/mfBulkInput/constants";
import cls from "./MflistExcelinput.module.css";

function ToolbarSection({ title, children }) {
  return (
    <Tooltip title={title} arrow placement="top">
      <div className={cls.toolbarSection}>{children}</div>
    </Tooltip>
  );
}

function ToolbarDivider() {
  return <div className={cls.toolbarDivider} aria-hidden />;
}

function ProductionToolbar({
  uploadedFileName,
  onSelectFile,
  onDownloadTemplate,
  onValidate,
  onReload,
  isValidating,
}) {
  const fileRef = useRef(null);
  return (
    <div className={cls.toolbar}>
      <button
        type="button"
        className={cls.btnAction}
        style={{ background: "#42a5f5", color: "#fff", border: "none" }}
        onClick={() => fileRef.current?.click()}
      >
        1. 엑셀 파일 선택
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelectFile(f);
          e.target.value = "";
        }}
      />
      {uploadedFileName && (
        <span className={cls.fileName}>✓ {uploadedFileName}</span>
      )}
      <button type="button" className={cls.btnAction} onClick={onDownloadTemplate}>
        양식 다운로드
      </button>
      <button
        type="button"
        className={`${cls.btnAction} ${cls.btnValidate}`}
        onClick={onValidate}
        disabled={isValidating}
      >
        {isValidating ? "검증 중…" : "검증 실행"}
      </button>
      <button type="button" className={cls.btnAction} onClick={onReload}>
        엑셀 다시 불러오기
      </button>
    </div>
  );
}

function TrainingToolbar({
  trainCount,
  setTrainCount,
  includeNonFire,
  setIncludeNonFire,
  trainRdvs,
  setTrainRdvs,
  trainRdvSelectOptions,
  trainRdvPartition,
  showAllTrainRdvs,
  setShowAllTrainRdvs,
  onGenerate,
  onClear,
  onValidate,
  onSaveConditions,
  isValidating,
}) {
  const nonFirePreview = Math.round((Number(trainCount) || 0) * TRAIN_NON_FIRE_RATIO);

  return (
    <div className={cls.toolbar}>
      <ToolbarSection title="생성할 훈련 데이터 행 수(1~100). 비소방 체크 시 전체의 20%를 의소대·경찰·군·공무원 항목으로 생성합니다.">
        <span style={{ fontSize: "0.78rem", fontWeight: 700 }}>개수 {trainCount}</span>
        <Slider
          size="small"
          min={1}
          max={100}
          value={Number(trainCount) || 50}
          onChange={(_, v) => setTrainCount(v)}
          sx={{ width: 120, mx: 1 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeNonFire}
              onChange={(e) => setIncludeNonFire(e.target.checked)}
              sx={{ py: 0 }}
            />
          }
          label={
            <span style={{ fontSize: "0.76rem", fontWeight: 700 }}>
              비소방항목 생성
              {includeNonFire && nonFirePreview > 0 ? ` (${nonFirePreview}건)` : ""}
            </span>
          }
          sx={{ m: 0, ml: 0.5 }}
        />
      </ToolbarSection>

      <ToolbarDivider />

      <ToolbarSection title="생성 데이터에 기본으로 채울 자원집결지를 선택합니다. 재난 위치와 가까운 집결지가 우선 표시됩니다.">
        <select
          value={trainRdvs}
          onChange={(e) => {
            const val = e.target.value;
            if (val === TRAIN_RDV_OPT_EXPAND) {
              setShowAllTrainRdvs(true);
              return;
            }
            if (val === TRAIN_RDV_OPT_COLLAPSE) {
              setShowAllTrainRdvs(false);
              return;
            }
            setTrainRdvs(val);
          }}
          style={{ fontSize: "0.78rem", maxWidth: 180 }}
        >
          <option value="">자원집결지 선택</option>
          {trainRdvPartition.showMore && showAllTrainRdvs && (
            <option value={TRAIN_RDV_OPT_COLLAPSE}>▲ 우선 집결지만</option>
          )}
          {trainRdvSelectOptions.map((r) => (
            <option key={r.id ?? r.rname} value={r.rname}>
              {r.rname}
            </option>
          ))}
          {trainRdvPartition.showMore && !showAllTrainRdvs && (
            <option value={TRAIN_RDV_OPT_EXPAND}>
              +더보기 ({trainRdvPartition.secondary.length})
            </option>
          )}
        </select>
      </ToolbarSection>

      <ToolbarDivider />

      <ToolbarSection title="훈련 데이터 생성·검증·조건 저장. DB 저장은 불가하며 시연용 엑셀만 가능합니다.">
        <Tooltip title="설정에 따라 훈련용 더미 행을 새로 생성합니다." arrow>
          <button
            type="button"
            className={`${cls.btnAction} ${cls.btnValidate}`}
            onClick={onGenerate}
          >
            훈련 데이터 생성
          </button>
        </Tooltip>
        <Tooltip title="그리드의 모든 행을 삭제합니다." arrow>
          <button type="button" className={cls.btnAction} onClick={onClear}>
            전체 지우기
          </button>
        </Tooltip>
        <Tooltip title="필수 항목·전화번호 형식 등을 검사합니다." arrow>
          <button
            type="button"
            className={`${cls.btnAction} ${cls.btnValidate}`}
            onClick={onValidate}
            disabled={isValidating}
          >
            {isValidating ? "검증 중…" : "검증 실행"}
          </button>
        </Tooltip>
        <Tooltip title="개수·집결지·비소방 옵션을 브라우저에 저장합니다." arrow>
          <button type="button" className={cls.btnAction} onClick={onSaveConditions}>
            조건 저장
          </button>
        </Tooltip>
      </ToolbarSection>
    </div>
  );
}

export default function BulkInputToolbar(props) {
  const {
    mode,
    apiAddr,
    rdvList,
    dsRdvRows,
    dsLocationHint,
    showAllTrainRdvs,
    setShowAllTrainRdvs,
    ...rest
  } = props;

  const trainRdvPartition = useMemo(
    () => buildTrainRdvPartition(rdvList, dsRdvRows, dsLocationHint),
    [rdvList, dsRdvRows, dsLocationHint],
  );

  const trainRdvSelectOptions = useMemo(() => {
    const { primary, secondary, showMore } = trainRdvPartition;
    let base;
    if (!showMore) base = [...rdvList];
    else if (showAllTrainRdvs) base = [...primary, ...secondary];
    else base = [...primary];
    const tv = (rest.trainRdvs || "").trim();
    if (tv && !base.some((r) => (r.rname || "").trim() === tv)) {
      base = [...base, { id: null, rname: tv, _synthetic: true }];
    }
    return base;
  }, [rdvList, trainRdvPartition, showAllTrainRdvs, rest.trainRdvs]);

  if (mode === "production") {
    return (
      <ProductionToolbar
        uploadedFileName={rest.uploadedFileName}
        onSelectFile={rest.onSelectFile}
        onDownloadTemplate={() => {
          window.open("/mfm_template.xlsx", "_blank");
        }}
        onValidate={rest.onValidate}
        onReload={() => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = ".xlsx";
          input.onchange = (e) => {
            const f = e.target.files?.[0];
            if (f) rest.onSelectFile(f);
          };
          input.click();
        }}
        isValidating={rest.isValidating}
      />
    );
  }

  return (
    <TrainingToolbar
      {...rest}
      trainRdvPartition={trainRdvPartition}
      trainRdvSelectOptions={trainRdvSelectOptions}
      showAllTrainRdvs={showAllTrainRdvs}
      setShowAllTrainRdvs={setShowAllTrainRdvs}
    />
  );
}

export function useRdvBootstrap(apiAddr) {
  const [rdvList, setRdvList] = React.useState([]);
  const [dsRdvRows, setDsRdvRows] = React.useState([]);
  const [dsLocationHint, setDsLocationHint] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    const myHq = (localStorage.hq || "").trim();
    const dsid =
      typeof sessionStorage !== "undefined"
        ? (sessionStorage.ondsid || "").trim()
        : "";

    const rdvReq = axios.get(`${apiAddr}/get/getrdvlist`);
    const dsReq = dsid
      ? axios.get(`${apiAddr}/get/getdsrdvs?dsid=${encodeURIComponent(dsid)}`)
      : Promise.resolve({ data: [] });
    const msgReq = dsid
      ? axios.get(`${apiAddr}/get/getmsg?dsid=${encodeURIComponent(dsid)}`)
      : Promise.resolve({ data: [] });

    Promise.all([rdvReq, dsReq, msgReq]).then(([rdvRes, dsRes, msgRes]) => {
      if (cancelled) return;
      const all = Array.isArray(rdvRes.data) ? rdvRes.data : [];
      setRdvList(
        all.filter((r) => {
          const rhq = (r.hq || "").trim();
          return !myHq || !rhq || rhq === myHq;
        }),
      );
      setDsRdvRows(Array.isArray(dsRes.data) ? dsRes.data : []);
      const msgArr = Array.isArray(msgRes.data) ? msgRes.data : [];
      const row0 = msgArr[0];
      setDsLocationHint([row0?.dsnm, row0?.dsaddr].filter(Boolean).join(" "));
    });

    return () => {
      cancelled = true;
    };
  }, [apiAddr]);

  return { rdvList, dsRdvRows, dsLocationHint };
}
